import { useCallback, useEffect, useMemo, useState, type ComponentProps, type FormEvent, type InputHTMLAttributes, type ReactNode } from 'react';
import {
  ArrowRight01Icon,
  Building03Icon,
  CalendarClockIcon,
  DashboardSquare03Icon,
  FileDownloadIcon,
  FileUploadIcon,
  FloppyDiskIcon,
  Globe02Icon,
  LicenseIcon,
  PackageReceiveIcon,
  Search01Icon,
  Ticket01Icon,
  UserAdd01Icon,
  UserGroupIcon,
  Shield01Icon,
} from '@hugeicons/core-free-icons';
import { Button } from '../../components/ui/Button';
import { CustomSelect } from '../../components/ui/CustomSelect';
import { DatePicker } from '../../components/ui/DatePicker';
import { HugeIcon } from '../../components/ui/HugeIcon';
import { Input } from '../../components/ui/Input';
import { StatusPill } from '../../components/ui/StatusPill';
import { TableActionButton } from '../../components/ui/TableActionButton';
import { TicketDetailPane } from '../tickets/TicketsPage';
import {
  TicketDirectoryCard,
  TicketDirectoryToolbar,
  type TicketPriorityFilter,
  type TicketStatusFilter,
} from '../tickets/TicketDirectory';
import {
  createPlatformMember,
  createPlatformDomain,
  createPlatformOrganization,
  decidePlatformUserRequest,
  deletePlatformTicketAttachment,
  fetchConsoleState,
  fetchPlatformIdentityHealth,
  replyToPlatformTicket,
  uploadPlatformTicketAttachment,
  upsertPlatformLicense,
  updatePlatformDomain,
  updatePlatformMember,
  updatePlatformTicketStatus,
  retryPlatformMemberSync,
  resendPlatformMemberInvitation,
  revokePlatformMemberInvitation,
  logoutUrl,
  type AppState,
  type PlatformOrganization,
  type PlatformState,
  type PlatformIdentityHealth,
  type Member,
  type UserImportCandidate,
  type UserImportInput,
  type UserImportValidation,
  type Notification,
  userImportInputSchema,
  userImportValidationSchema,
} from '../../lib/api';
import { cn } from '../../lib/cn';
import { ConsoleSidebar, type ConsoleSidebarItem } from '../../components/layout/ConsoleSidebar';
import { NotificationCenter } from '../../components/layout/NotificationCenter';
import { SegmentedMeter } from '../../components/layout/SegmentedMeter';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '../../components/ui/sidebar';
import { HeaderUserMenu } from '../../components/layout/HeaderUserMenu';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { OperationalTableCard } from '../../components/ui/operational-table-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Pagination, PaginationControls, PaginationNext, PaginationPrevious, PaginationStatus } from '../../components/ui/pagination';
import { PlatformBuildInbox, PlatformPublishRelease, PlatformReleaseHistory } from './PlatformReleaseWorkspaces';
import { getLicensePeriodProgress } from '../../lib/licensePeriod';
import { downloadMembersCsv, UserImportDialog } from '../users/UserImportDialog';

type Role = 'org_admin' | 'member';
type UserStatus = 'active' | 'pending_activation' | 'revoked';
type LicenseStatus = 'active' | 'expiring' | 'expired' | 'suspended';
type DeploymentModel = 'saas_hybrid' | 'on_prem';
type ReleaseChannel = 'stable' | 'beta';
type PlatformView = 'overview' | 'customers' | 'customer-directory' | 'releases-inbox' | 'releases-publish' | 'releases-history' | 'tickets';
type CustomerTab = 'overview' | 'domains' | 'users' | 'license';
type PlatformSidebarView = PlatformView | 'customers-root' | 'releases-root' | 'customer-detail' | `customer-${CustomerTab}` | `tickets-${TicketStatusFilter}`;

const domainEnvironmentOptions = [
  { value: 'production', label: 'Production' },
  { value: 'staging', label: 'Staging' },
  { value: 'local', label: 'Local' },
] as const;

const roleOptions = [
  { value: 'member', label: 'Member' },
  { value: 'org_admin', label: 'Admin' },
] as const;

const userStatusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'pending_activation', label: 'Pending activation' },
  { value: 'revoked', label: 'Revoked' },
] as const;

const licenseStatusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'expiring', label: 'Expiring' },
  { value: 'expired', label: 'Expired' },
  { value: 'suspended', label: 'Suspended' },
] as const;

const deploymentModelOptions = [
  { value: 'saas_hybrid', label: 'SaaS / Hybrid · Managed identity' },
  { value: 'on_prem', label: 'On-prem · License file' },
] as const;

const releaseChannelOptions = [
  { value: 'stable', label: 'Stable' },
  { value: 'beta', label: 'Beta' },
] as const;

const platformNavItems: Array<ConsoleSidebarItem<PlatformSidebarView>> = [
  { id: 'overview', label: 'Overview', icon: DashboardSquare03Icon },
  { id: 'customers-root', label: 'Customers', icon: Building03Icon, children: [
    { id: 'customers', label: 'Customer overview' },
    { id: 'customer-directory', label: 'Directory' },
  ] },
  { id: 'releases-root', label: 'Releases', icon: PackageReceiveIcon, children: [
    { id: 'releases-inbox', label: 'Build inbox' },
    { id: 'releases-publish', label: 'Publish release' },
    { id: 'releases-history', label: 'Release history' },
  ] },
  { id: 'tickets', label: 'Tickets', icon: Ticket01Icon, children: [
    { id: 'tickets-all', label: 'All tickets' },
    { id: 'tickets-open', label: 'Open' },
    { id: 'tickets-in_progress', label: 'In progress' },
    { id: 'tickets-closed', label: 'Closed' },
  ] },
];

const platformViewCopy: Record<PlatformView, { title: string; description: string }> = {
  overview: {
    title: 'Overview',
    description: 'Monitor customer access, licensing, domains, and active support load.',
  },
  customers: {
    title: 'Customers',
    description: 'Monitor customer status, licensing, deployments, and access.',
  },
  'customer-directory': { title: 'Customer directory', description: 'Find, create, and open customer organizations.' },
  'releases-inbox': { title: 'Build inbox', description: 'Receive and classify verified myPDF build outputs.' },
  'releases-publish': { title: 'Publish release', description: 'Release a classified build to all or selected eligible customers.' },
  'releases-history': { title: 'Release history', description: 'Review build status, channels, audiences, and publication history.' },
  tickets: {
    title: 'Tickets',
    description: 'Manage customer support conversations and resolve open tickets.',
  },
};

const PLATFORM_TICKETS_PER_PAGE = 15;

const platformViewPaths: Record<PlatformView, string> = {
  overview: '/platform',
  customers: '/platform/customers',
  'customer-directory': '/platform/customers/directory',
  'releases-inbox': '/platform/releases',
  'releases-publish': '/platform/releases/publish',
  'releases-history': '/platform/releases/history',
  tickets: '/platform/tickets',
};

function platformViewFromLocation(): PlatformView {
  if (typeof window === 'undefined') return 'overview';

  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  if (normalizedPath === '/platform/licenses') return 'customers';
  const matchedView = (Object.entries(platformViewPaths) as Array<[PlatformView, string]>).find(
    ([, path]) => path === normalizedPath
  );

  return matchedView?.[0] ?? 'overview';
}

function customerIdFromLocation() {
  if (typeof window === 'undefined' || window.location.pathname.replace(/\/+$/, '') !== platformViewPaths.customers) return null;
  return new URLSearchParams(window.location.search).get('customer');
}

function customerTabFromLocation(): CustomerTab {
  if (typeof window === 'undefined' || window.location.pathname.replace(/\/+$/, '') !== platformViewPaths.customers) return 'overview';
  const requestedTab = new URLSearchParams(window.location.search).get('tab');
  return requestedTab === 'domains' || requestedTab === 'users' || requestedTab === 'license'
    ? requestedTab
    : 'overview';
}

function buildPlatformNotifications(state: PlatformState, readAt: Record<string, string>): Notification[] {
  const notifications: Notification[] = [];
  for (const organization of state.organizations) {
    for (const request of organization.userRequests.filter((item) => item.status === 'pending_management_approval')) {
      const id = `approval:${organization.id}:${request.id}`;
      notifications.push({
        id,
        type: 'approval',
        title: `Access approval · ${organization.name}`,
        body: `${request.fullName} requested ${request.requestedRole === 'org_admin' ? 'administrator' : 'member'} access.`,
        actionPath: `/platform/customers?customer=${encodeURIComponent(organization.id)}&tab=users`,
        createdAt: request.createdAt,
        readAt: readAt[id] ?? null,
      });
    }
    for (const ticket of organization.tickets.filter((item) => item.status !== 'closed')) {
      const id = `ticket:${organization.id}:${ticket.id}`;
      notifications.push({
        id,
        type: 'ticket',
        title: ticket.subject,
        body: `${organization.name} · ${ticket.status === 'in_progress' ? 'In progress' : 'Needs review'}`,
        actionPath: platformViewPaths.tickets,
        createdAt: ticket.updatedAt,
        readAt: readAt[id] ?? null,
      });
    }
  }
  for (const release of state.releases.filter((item) => item.status === 'published' && item.publishedAt)) {
    const id = `release:${release.id}`;
    notifications.push({
      id,
      type: 'release',
      title: `myPDF ${release.version} published`,
      body: `${release.channel === 'stable' ? 'Stable' : 'Beta'} · ${release.targetCount} customer targets`,
      actionPath: platformViewPaths['releases-history'],
      createdAt: release.publishedAt ?? new Date(0).toISOString(),
      readAt: readAt[id] ?? null,
    });
  }
  return [...notifications].sort((left, right) => right.createdAt.localeCompare(left.createdAt)).slice(0, 30);
}

export function PlatformPage({
  state,
  onStateChange,
}: {
  state: PlatformState;
  onStateChange: (state: AppState) => void;
}) {
  const [view, setView] = useState<PlatformView>(() => platformViewFromLocation());
  const [customerDetailId, setCustomerDetailId] = useState<string | null>(() => customerIdFromLocation());
  const [customerTab, setCustomerTab] = useState<CustomerTab>(() => customerTabFromLocation());
  const [ticketStatusFilter, setTicketStatusFilter] = useState<TicketStatusFilter>('all');
  const [message, setMessage] = useState<string | null>(null);
  const [identityHealth, setIdentityHealth] = useState<PlatformIdentityHealth | null>(null);
  const [notificationReadAt, setNotificationReadAt] = useState<Record<string, string>>({});
  const copy = platformViewCopy[view];
  const customerDetailOrganization = useMemo(
    () => state.organizations.find((organization) => organization.id === customerDetailId) ?? null,
    [customerDetailId, state.organizations]
  );
  const notifications = useMemo(
    () => buildPlatformNotifications(state, notificationReadAt),
    [notificationReadAt, state]
  );

  const navigateToView = useCallback((nextView: PlatformView) => {
    setView(nextView);
    setCustomerDetailId(null);
    setCustomerTab('overview');

    if (typeof window === 'undefined') return;
    const nextPath = platformViewPaths[nextView];
    if (`${window.location.pathname}${window.location.search}` !== nextPath) {
      window.history.pushState(null, '', nextPath);
    }
  }, []);

  const openCustomerDetail = useCallback((organizationId: string) => {
    setView('customers');
    setCustomerDetailId(organizationId);
    setCustomerTab('overview');
    if (typeof window !== 'undefined') {
      window.history.pushState(null, '', `${platformViewPaths.customers}?customer=${encodeURIComponent(organizationId)}&tab=overview`);
    }
  }, []);

  const changeCustomerTab = useCallback((nextTab: CustomerTab) => {
    setCustomerTab(nextTab);
    if (typeof window !== 'undefined' && customerDetailId) {
      window.history.pushState(null, '', `${platformViewPaths.customers}?customer=${encodeURIComponent(customerDetailId)}&tab=${nextTab}`);
    }
  }, [customerDetailId]);

  const closeCustomerDetail = useCallback(() => {
    setCustomerDetailId(null);
    setCustomerTab('overview');
    if (typeof window !== 'undefined' && window.location.pathname === platformViewPaths.customers) {
      window.history.pushState(null, '', platformViewPaths.customers);
    }
  }, []);

  const openPlatformNotification = useCallback((notification: Notification) => {
    setNotificationReadAt((current) => current[notification.id] ? current : { ...current, [notification.id]: new Date().toISOString() });
    if (!notification.actionPath) return;
    const target = new URL(notification.actionPath, window.location.origin);
    if (target.pathname === platformViewPaths.customers) {
      const organizationId = target.searchParams.get('customer');
      const requestedTab = target.searchParams.get('tab');
      if (organizationId) {
        setView('customers');
        setCustomerDetailId(organizationId);
        setCustomerTab(requestedTab === 'users' || requestedTab === 'domains' || requestedTab === 'license' ? requestedTab : 'overview');
        window.history.pushState(null, '', `${target.pathname}${target.search}`);
      }
      return;
    }
    if (target.pathname === platformViewPaths.tickets) navigateToView('tickets');
    else if (target.pathname.startsWith('/platform/releases')) navigateToView('releases-history');
  }, [navigateToView]);

  const readAllPlatformNotifications = useCallback(() => {
    const readAt = new Date().toISOString();
    setNotificationReadAt(Object.fromEntries(notifications.map((notification) => [notification.id, readAt])));
  }, [notifications]);

  async function refresh(nextMessage: string) {
    onStateChange(await fetchConsoleState());
    if (view === 'overview') {
      fetchPlatformIdentityHealth().then(setIdentityHealth).catch(() => setIdentityHealth(null));
    }
    setMessage(nextMessage);
  }

  useEffect(() => {
    if (view !== 'overview') {
      setIdentityHealth(null);
      return;
    }
    let active = true;
    fetchPlatformIdentityHealth()
      .then((health) => { if (active) setIdentityHealth(health); })
      .catch(() => { if (active) setIdentityHealth(null); });
    return () => { active = false; };
  }, [view]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
    if (!Object.values(platformViewPaths).includes(currentPath)) {
      window.history.replaceState(null, '', platformViewPaths[platformViewFromLocation()]);
    }

    function syncViewFromHistory() {
      setView(platformViewFromLocation());
      setCustomerDetailId(customerIdFromLocation());
      setCustomerTab(customerTabFromLocation());
    }

    window.addEventListener('popstate', syncViewFromHistory);
    return () => window.removeEventListener('popstate', syncViewFromHistory);
  }, []);

  const content = (
    <>
      {view === 'overview' ? (
        <PlatformOverview identityHealth={identityHealth} state={state} setView={navigateToView} />
      ) : null}

      {view === 'customers' ? (
        customerDetailOrganization ? <CustomerOperations activeTab={customerTab} onChanged={refresh} onTabChange={changeCustomerTab} organization={customerDetailOrganization} /> : <PlatformCustomersOverview onOpenDirectory={() => navigateToView('customer-directory')} onSelect={openCustomerDetail} organizations={state.organizations} />
      ) : null}

      {view === 'customer-directory' ? <PlatformCustomersDirectory onCreated={() => refresh('Organization created.')} onSelect={openCustomerDetail} organizations={state.organizations} /> : null}

      {view === 'tickets' ? (
        <PlatformTicketsWorkspace
          onChanged={refresh}
          onStatusFilterChange={setTicketStatusFilter}
          organizations={state.organizations}
          statusFilter={ticketStatusFilter}
        />
      ) : null}

      {view === 'releases-inbox' ? <PlatformBuildInbox onChanged={refresh} releases={state.releases} /> : null}
      {view === 'releases-publish' ? <PlatformPublishRelease onChanged={refresh} organizations={state.organizations} releases={state.releases} /> : null}
      {view === 'releases-history' ? <PlatformReleaseHistory releases={state.releases} /> : null}
    </>
  );
  const usesConstrainedWorkspace = view === 'tickets';
  const sidebarNavItems = useMemo(() => {
    const openTickets = state.organizations.reduce((count, organization) => count + organization.tickets.filter((ticket) => ticket.status === 'open').length, 0);
    const inProgressTickets = state.organizations.reduce((count, organization) => count + organization.tickets.filter((ticket) => ticket.status === 'in_progress').length, 0);

    return platformNavItems.map((item) => {
      if (item.id === 'tickets') {
        return {
          ...item,
          children: item.children?.map((child) => child.id === 'tickets-open'
            ? { ...child, count: openTickets }
            : child.id === 'tickets-in_progress'
              ? { ...child, count: inProgressTickets }
              : child),
        };
      }
      if (item.id === 'customers-root' && customerDetailOrganization) {
        return {
          ...item,
          children: item.children?.map((child) => child.id === 'customers'
            ? {
                ...child,
                children: [{
                  id: 'customer-detail' as const,
                  label: customerDetailOrganization.name,
                  children: [
                    { id: 'customer-overview' as const, label: 'Overview' },
                    { id: 'customer-domains' as const, label: 'Domains' },
                    { id: 'customer-users' as const, label: 'Users' },
                    { id: 'customer-license' as const, label: 'License' },
                  ],
                }],
              }
            : child),
        };
      }
      return item;
    });
  }, [customerDetailOrganization, state.organizations]);
  const activeSidebarView: PlatformSidebarView = view === 'tickets'
    ? `tickets-${ticketStatusFilter}`
    : customerDetailOrganization
      ? `customer-${customerTab}`
      : view;

  const navigateFromSidebar = useCallback((nextView: PlatformSidebarView) => {
    if (nextView === 'customer-detail') {
      changeCustomerTab('overview');
      return;
    }
    if (nextView.startsWith('customer-') && nextView !== 'customer-directory') {
      changeCustomerTab(nextView.slice('customer-'.length) as CustomerTab);
      return;
    }
    if (nextView.startsWith('tickets-')) {
      setTicketStatusFilter(nextView.slice('tickets-'.length) as TicketStatusFilter);
      navigateToView('tickets');
      return;
    }
    if (nextView === 'tickets') setTicketStatusFilter('all');
    if (nextView === 'customers-root') {
      navigateToView('customers');
      return;
    }
    if (nextView === 'releases-root') {
      navigateToView('releases-inbox');
      return;
    }
    navigateToView(nextView as PlatformView);
  }, [changeCustomerTab, navigateToView]);

  return (
    <SidebarProvider
      className={cn(
        'bg-[var(--color-page)] text-slate-900',
        usesConstrainedWorkspace && 'h-dvh min-h-0 overflow-hidden'
      )}
    >
      <ConsoleSidebar
        activeView={activeSidebarView}
        label="Lunex platform"
        navItems={sidebarNavItems}
        onNavigate={navigateFromSidebar}
        subtitle="Super admin console"
      />
      <SidebarInset
        className={cn(
          'w-auto! min-w-0 bg-background md:m-0 md:rounded-none md:shadow-none',
          usesConstrainedWorkspace && 'h-dvh min-h-0 overflow-hidden'
        )}
      >
        <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center justify-between gap-3 border-b bg-card px-3 sm:px-4">
          <div className="flex min-w-0 items-center gap-3">
            <SidebarTrigger className="shrink-0" />
            <h1 className="truncate text-sm font-semibold text-foreground">
              {view === 'customers' && customerDetailOrganization ? customerDetailOrganization.name : copy.title}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <NotificationCenter notifications={notifications} onOpenNotification={openPlatformNotification} onReadAll={readAllPlatformNotifications} />
            <HeaderUserMenu currentUser={state.currentUser} logoutHref={logoutUrl()} />
          </div>
        </header>

        <main
          className={cn(
            'mx-auto flex w-full max-w-[1600px] min-w-0 flex-1 flex-col gap-5 p-3 sm:p-4 lg:p-6',
            usesConstrainedWorkspace && 'min-h-0 overflow-y-auto lg:overflow-hidden'
          )}
        >
          {customerDetailOrganization ? (
            <nav aria-label="Breadcrumb" className="flex min-w-0 shrink-0 items-center gap-2 text-sm font-medium text-muted-foreground">
              <button className="rounded-md px-1 py-1 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30" onClick={closeCustomerDetail} type="button">Customers</button>
              <span aria-hidden="true">/</span>
              <span aria-current="page" className="truncate text-foreground">{customerDetailOrganization.name}</span>
            </nav>
          ) : null}
          {message ? <p aria-live="polite" className="shrink-0 text-xs font-medium text-emerald-700">{message}</p> : null}
          {customerDetailOrganization ? (
            <div className="min-w-0 flex-1">{content}</div>
          ) : (
            <div className={cn('min-w-0 flex-1', usesConstrainedWorkspace && 'min-h-0')}>{content}</div>
          )}
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

function PlatformOverview({
  identityHealth,
  state,
  setView,
}: {
  identityHealth: PlatformIdentityHealth | null;
  state: PlatformState;
  setView: (view: PlatformView) => void;
}) {
  const customers = state.organizations.length;
  const activeLicenses = state.organizations.filter(
    (organization) => organization.license?.status === 'active'
  ).length;
  const openTickets = state.organizations.reduce(
    (sum, organization) =>
      sum + organization.tickets.filter((ticket) => ticket.status !== 'closed').length,
    0
  );
  const pendingApprovals = state.organizations.reduce(
    (sum, organization) =>
      sum + organization.userRequests.filter((request) => request.status === 'pending_management_approval').length,
    0
  );
  const domains = state.organizations.reduce(
    (sum, organization) => sum + organization.domains.filter((domain) => domain.active).length,
    0
  );
  const openTicketRecords = state.organizations
    .flatMap((organization) => organization.tickets.map((ticket) => ({ organization, ticket })))
    .filter(({ ticket }) => ticket.status !== 'closed')
    .sort((left, right) => right.ticket.updatedAt.localeCompare(left.ticket.updatedAt));

  return (
    <section className="flex flex-col gap-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard detail="Organizations managed by Lunex" icon={Building03Icon} iconTone="bg-teal-500/12 text-teal-700" label="Customers" onClick={() => setView('customers')} value={String(customers)} />
        <MetricCard detail="Managed inside each customer workspace" icon={LicenseIcon} iconTone="bg-violet-500/12 text-violet-700" label="Active licenses" onClick={() => setView('customers')} value={String(activeLicenses)} />
        <MetricCard detail="Customer access requests awaiting review" icon={UserAdd01Icon} iconTone="bg-amber-500/14 text-amber-700" label="Pending approvals" onClick={() => setView('customers')} value={String(pendingApprovals)} />
        <MetricCard detail="Authorized active deployments" icon={Globe02Icon} iconTone="bg-sky-500/12 text-sky-700" label="Active domains" onClick={() => setView('customers')} value={String(domains)} />
      </div>

      <PlatformAuthenticationHealth health={identityHealth} />

      <div className="grid items-start gap-4 2xl:grid-cols-[minmax(0,1.65fr)_minmax(360px,0.75fr)]">
        <OperationalTableCard
          action={<Button onClick={() => setView('customers')} size="sm" variant="outline">Manage customers</Button>}
          information={`${customers} customer ${customers === 1 ? 'organization is' : 'organizations are'} configured.`}
          informationLabel="Customer directory information"
          title="Customer operations"
        >
          <Table className="min-w-[680px]">
            <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="pl-4">Customer</TableHead><TableHead>Primary domain</TableHead><TableHead>Users</TableHead><TableHead>License</TableHead><TableHead className="pr-4 text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {state.organizations.slice(0, 5).map((organization) => (
                <TableRow key={organization.id}>
                  <TableCell className="pl-4"><p className="font-semibold">{organization.name}</p><p className="text-xs text-muted-foreground">{organization.slug}</p></TableCell>
                  <TableCell>{organization.primaryDomain || 'No active domain'}</TableCell>
                  <TableCell>{organization.members.length}</TableCell>
                  <TableCell><StatusPill status={organization.license?.status ?? 'not configured'} /></TableCell>
                  <TableCell className="pr-4 text-right"><TableActionButton onClick={() => setView('customers')}>Review</TableActionButton></TableCell>
                </TableRow>
              ))}
              {!customers ? <TableRow><TableCell className="h-20 text-center text-muted-foreground" colSpan={5}>No customers configured.</TableCell></TableRow> : null}
            </TableBody>
          </Table>
        </OperationalTableCard>

        <SupportOverviewCard onOpen={() => setView('tickets')} openTickets={openTickets} records={openTicketRecords} />
      </div>
    </section>
  );
}

function PlatformAuthenticationHealth({ health }: { health: PlatformIdentityHealth | null }) {
  const max = Math.max(1, ...(health?.series.map((point) => point.successful + point.failed + point.risky) ?? [1]));
  return (
    <div className="space-y-5">
      <section className="space-y-5">
        <div><h2 className="font-heading text-base font-medium">Authentication health</h2><p className="mt-1 text-xs text-muted-foreground">Thirty-day cross-organization totals with privacy-safe tenant attribution.</p></div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <IdentityStat label="Successful" value={health ? String(health.summary.successful) : '—'} />
          <IdentityStat label="Failed" value={health ? String(health.summary.failed) : '—'} />
          <IdentityStat label="Risky" value={health ? String(health.summary.risky) : '—'} />
          <IdentityStat label="Affected organizations" value={health ? String(health.summary.affectedOrganizations) : '—'} />
        </div>
        <div><div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground"><span>Daily authentication activity</span><span className="flex items-center gap-3"><span className="flex items-center gap-1.5"><i className="size-2 rounded-full bg-teal-500" />Successful</span><span className="flex items-center gap-1.5"><i className="size-2 rounded-full bg-red-400" />Failed</span><span className="flex items-center gap-1.5"><i className="size-2 rounded-full bg-amber-400" />Risky</span><strong className="font-medium text-foreground">{health?.summary.successRate ?? '—'}%</strong></span></div><div className="flex h-36 items-end gap-1 rounded-xl border bg-white px-3 pb-3 pt-5" role="img" aria-label="Platform authentication health chart">{(health?.series ?? Array.from({ length: 30 }, (_, index) => ({ date: String(index), successful: 0, failed: 0, risky: 0 }))).map((point) => { const total = point.successful + point.failed + point.risky; return <div className="flex h-full min-w-0 flex-1 flex-col justify-end" key={point.date} title={`${point.date}: ${point.successful} successful, ${point.failed} failed, ${point.risky} risky`}><div className="w-full overflow-hidden rounded-sm bg-muted" style={{ height: `${Math.max(total ? 5 : 2, (total / max) * 100)}%` }}><div className="bg-teal-500" style={{ height: `${total ? point.successful / total * 100 : 0}%` }} /><div className="bg-red-400" style={{ height: `${total ? point.failed / total * 100 : 0}%` }} /><div className="bg-amber-400" style={{ height: `${total ? point.risky / total * 100 : 0}%` }} /></div></div>; })}</div></div>
      </section>
      <OperationalTableCard information="Oldest synchronization checks and active drift are shown first." informationLabel="Identity diagnostics information" title="Identity synchronization">
        <Table className="min-w-[760px]"><TableHeader><TableRow className="hover:bg-transparent"><TableHead className="pl-4">Organization</TableHead><TableHead>Sync state</TableHead><TableHead>Awaiting</TableHead><TableHead>Drifted</TableHead><TableHead>Retries</TableHead><TableHead className="pr-4">Last checked</TableHead></TableRow></TableHeader><TableBody>{health?.organizations.slice(0, 5).map((organization) => <TableRow key={organization.organizationId}><TableCell className="pl-4 font-semibold">{organization.organizationName}</TableCell><TableCell><StatusPill status={formatWorkOSSyncState(organization.syncState)} /></TableCell><TableCell>{organization.awaitingMembers}</TableCell><TableCell>{organization.driftedMembers}</TableCell><TableCell>{organization.retryCount}</TableCell><TableCell className="pr-4 text-xs text-muted-foreground">{organization.lastCheckedAt ? formatDate(organization.lastCheckedAt) : 'Not checked'}</TableCell></TableRow>)}{!health?.organizations.length ? <TableRow><TableCell className="h-16 text-center text-muted-foreground" colSpan={6}>Identity diagnostics are not available yet.</TableCell></TableRow> : null}</TableBody></Table>
      </OperationalTableCard>
    </div>
  );
}

function IdentityStat({ label, value }: { label: string; value: string }) {
  return <div className="rounded-xl border bg-card p-3"><div className="flex items-center gap-2"><span className="flex size-7 items-center justify-center rounded-lg bg-sky-500/12 text-sky-700"><HugeIcon icon={Shield01Icon} size={15} /></span><p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p></div><p className="mt-3 truncate text-lg font-semibold">{value}</p></div>;
}

function formatWorkOSSyncState(state: PlatformIdentityHealth['organizations'][number]['syncState']) {
  if (state === 'awaiting_sync') return 'Awaiting sync';
  if (state === 'drifted') return 'Drift detected';
  if (state === 'failed') return 'Sync failed';
  if (state === 'unlinked') return 'Not linked';
  return 'Synced';
}

function SupportOverviewCard({
  onOpen,
  openTickets,
  records,
}: {
  onOpen: () => void;
  openTickets: number;
  records: Array<{ organization: PlatformOrganization; ticket: PlatformOrganization['tickets'][number] }>;
}) {
  const inProgress = records.filter(({ ticket }) => ticket.status === 'in_progress').length;
  const activeShare = openTickets ? Math.round((inProgress / openTickets) * 100) : 0;
  return (
    <Card className="gap-0 bg-[var(--panel-header)] py-0" size="sm">
      <CardHeader className="min-h-14 content-center items-center bg-[var(--panel-header)] py-0">
        <CardTitle className="flex items-center gap-2 text-sm uppercase tracking-wide">Support workload</CardTitle>
        <CardAction className="self-center"><Button onClick={onOpen} size="sm" variant="outline">Open support queue</Button></CardAction>
      </CardHeader>
      <CardContent className="grid gap-0 border-t bg-card p-0 md:grid-cols-2 2xl:flex 2xl:flex-col">
        <div className="flex items-center justify-center gap-4 p-4 md:justify-start md:gap-6 md:p-5 2xl:justify-center">
          <div className="relative grid size-28 shrink-0 place-items-center rounded-full sm:size-32" style={{ background: `conic-gradient(#14b8a6 0 ${activeShare}%, #8b5cf6 ${activeShare}% 72%, #f59e0b 72% 100%)` }}>
            <div className="grid size-20 place-items-center rounded-full bg-card text-center shadow-inner sm:size-[88px]"><div><p className="text-3xl font-semibold tracking-tight">{openTickets}</p><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Open tickets</p></div></div>
          </div>
          <div className="min-w-[170px] flex-1 space-y-3 text-sm">
            <p className="flex items-center gap-2.5 whitespace-nowrap"><span className="size-2 rounded-full bg-teal-500" />In progress <strong className="ml-auto pl-3">{inProgress}</strong></p>
            <p className="flex items-center gap-2.5 whitespace-nowrap"><span className="size-2 rounded-full bg-violet-500" />Needs review <strong className="ml-auto pl-3">{Math.max(0, openTickets - inProgress)}</strong></p>
            <p className="flex items-center gap-2.5 whitespace-nowrap"><span className="size-2 rounded-full bg-amber-500" />Customers <strong className="ml-auto pl-3">{new Set(records.map(({ organization }) => organization.id)).size}</strong></p>
          </div>
        </div>
        <div className="divide-y border-t bg-white px-4 md:border-l md:border-t-0 2xl:border-l-0 2xl:border-t">
          {records.slice(0, 3).map(({ organization, ticket }) => <button className="flex w-full items-center gap-4 py-4 text-left" key={`${organization.id}:${ticket.id}`} onClick={onOpen} type="button"><span className="min-w-0 flex-1"><span className="block truncate font-semibold">{ticket.subject}</span><span className="mt-1 block truncate text-xs text-muted-foreground">{organization.name}</span></span><StatusPill status={ticket.status} /></button>)}
          {!records.length ? <p className="py-6 text-center text-sm text-muted-foreground">Support is up to date.</p> : null}
        </div>
      </CardContent>
    </Card>
  );
}

function PlatformCustomersOverview({
  onOpenDirectory,
  onSelect,
  organizations,
}: {
  onOpenDirectory: () => void;
  onSelect: (organizationId: string) => void;
  organizations: PlatformOrganization[];
}) {
  const healthy = organizations.filter((organization) => organization.status === 'active' && organization.license?.status === 'active').length;
  const licenseAttention = organizations.filter((organization) => !organization.license || organization.license.status !== 'active').length;
  const pendingApprovals = organizations.reduce((sum, organization) => sum + organization.userRequests.filter((request) => request.status === 'pending_management_approval').length, 0);
  return (
    <section className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard detail="Organizations managed by Lunex" icon={Building03Icon} iconTone="bg-teal-500/12 text-teal-700" label="Customers" onClick={onOpenDirectory} value={String(organizations.length)} />
        <MetricCard detail="Active organization and license" icon={LicenseIcon} iconTone="bg-emerald-500/12 text-emerald-700" label="Healthy" onClick={onOpenDirectory} value={String(healthy)} />
        <MetricCard detail="Missing or non-active licenses" icon={CalendarClockIcon} iconTone="bg-rose-500/12 text-rose-700" label="License attention" onClick={onOpenDirectory} value={String(licenseAttention)} />
        <MetricCard detail="Customer access requests to review" icon={UserAdd01Icon} iconTone="bg-amber-500/14 text-amber-700" label="Pending approvals" onClick={onOpenDirectory} value={String(pendingApprovals)} />
      </div>
      <OperationalTableCard information="The default customer view summarizes operational readiness before opening an individual customer workspace." informationLabel="Customer status information" title="Customer status">
        <Table className="min-w-[860px]">
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="pl-4">Customer</TableHead><TableHead>Organization</TableHead><TableHead>License</TableHead><TableHead>Users</TableHead><TableHead>Domains</TableHead><TableHead>Access queue</TableHead><TableHead className="pr-4 text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>{organizations.map((organization) => <TableRow className="cursor-pointer" key={organization.id} onClick={() => onSelect(organization.id)}><TableCell className="pl-4"><p className="font-semibold">{organization.name}</p><p className="text-xs text-muted-foreground">{organization.primaryDomain || organization.slug}</p></TableCell><TableCell><StatusPill status={organization.status} /></TableCell><TableCell><StatusPill status={organization.license?.status ?? 'Not configured'} /></TableCell><TableCell>{organization.members.length}</TableCell><TableCell>{organization.domains.filter((domain) => domain.active).length} / {organization.domains.length}</TableCell><TableCell>{organization.userRequests.filter((request) => request.status === 'pending_management_approval').length}</TableCell><TableCell className="pr-4 text-right"><TableActionButton onClick={(event) => { event.stopPropagation(); onSelect(organization.id); }}>Open customer</TableActionButton></TableCell></TableRow>)}</TableBody>
        </Table>
      </OperationalTableCard>
    </section>
  );
}

function PlatformCustomersDirectory({
  onCreated,
  onSelect,
  organizations,
}: {
  onCreated: () => Promise<void>;
  onSelect: (organizationId: string) => void;
  organizations: PlatformOrganization[];
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const filteredOrganizations = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return organizations;
    return organizations.filter((organization) =>
      [organization.name, organization.slug, organization.primaryDomain]
        .some((value) => value.toLowerCase().includes(normalized))
    );
  }, [organizations, query]);
  const pageCount = Math.max(1, Math.ceil(filteredOrganizations.length / 15));
  const pagedOrganizations = filteredOrganizations.slice((page - 1) * 15, page * 15);
  useEffect(() => { setPage(1); }, [query]);
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);

  return (
    <section className="grid min-h-0 gap-5">
      {organizations.length ? (
        <OperationalTableCard
          action={
            <div className="flex w-full min-w-0 flex-nowrap items-center gap-2 sm:w-auto">
              <label className="relative min-w-0 flex-1 sm:w-56 sm:flex-none lg:w-72">
                <span className="sr-only">Search customers</span>
                <HugeIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" icon={Search01Icon} size={14} />
                <Input className="h-8 w-full bg-white pl-9" onChange={(event) => setQuery(event.target.value)} placeholder="Search customers" value={query} />
              </label>
              <Button aria-label="Create customer" onClick={() => setCreateOpen(true)} size="icon-sm" title="Create customer"><HugeIcon icon={UserAdd01Icon} size={15} /></Button>
            </div>
          }
          information={`${filteredOrganizations.length} of ${organizations.length} customer organizations shown.`}
          informationLabel="Customer directory information"
          title="Customer directory"
        >
          <Table className="min-w-[820px]">
            <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="pl-4">Customer</TableHead><TableHead>Primary domain</TableHead><TableHead>Users</TableHead><TableHead>Domains</TableHead><TableHead>License</TableHead><TableHead>Status</TableHead><TableHead className="pr-4 text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {pagedOrganizations.map((organization) => (
                <TableRow
                  className="cursor-pointer focus-visible:outline-none focus-visible:[&>td]:bg-muted/70"
                  key={organization.id}
                  onClick={() => onSelect(organization.id)}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelect(organization.id); } }}
                  tabIndex={0}
                >
                  <TableCell className="pl-4"><p className="font-semibold">{organization.name}</p><p className="text-xs text-muted-foreground">{organization.slug}</p></TableCell>
                  <TableCell>{organization.primaryDomain || 'No active domain'}</TableCell>
                  <TableCell>{organization.members.length}</TableCell>
                  <TableCell>{organization.domains.length}</TableCell>
                  <TableCell><StatusPill status={organization.license?.status ?? 'not configured'} /></TableCell>
                  <TableCell><StatusPill status={organization.status} /></TableCell>
                  <TableCell className="pr-4 text-right"><Button aria-label={`Open ${organization.name}`} onClick={(event) => { event.stopPropagation(); onSelect(organization.id); }} size="icon-sm" variant="ghost"><HugeIcon icon={ArrowRight01Icon} size={15} /></Button></TableCell>
                </TableRow>
              ))}
              {!filteredOrganizations.length ? <TableRow><TableCell className="h-24 text-center text-muted-foreground" colSpan={7}>No customers match this search.</TableCell></TableRow> : null}
            </TableBody>
          </Table>
          {filteredOrganizations.length > 15 ? <Pagination className="border-t px-4 py-3"><PaginationStatus>Page {page} of {pageCount} · {filteredOrganizations.length} customers</PaginationStatus><PaginationControls><PaginationPrevious disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))} /><PaginationNext disabled={page === pageCount} onClick={() => setPage((current) => Math.min(pageCount, current + 1))} /></PaginationControls></Pagination> : null}
        </OperationalTableCard>
      ) : (
        <EmptyCustomersState onCreate={() => setCreateOpen(true)} />
      )}

      <CreateOrganizationDialog
        onClose={() => setCreateOpen(false)}
        onCreated={async () => {
          await onCreated();
          setCreateOpen(false);
        }}
        open={createOpen}
      />
    </section>
  );
}

function EmptyCustomersState({ onCreate }: { onCreate?: () => void }) {
  return (
    <Card className="flex min-h-64 items-center justify-center p-8 text-center" size="sm">
      <span className="mb-4 inline-flex size-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
        <HugeIcon icon={Building03Icon} size={24} />
      </span>
      <h2 className="text-lg font-semibold">No customers yet</h2>
      <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">Create the first customer to configure domains, members, and licensing.</p>
      {onCreate ? <Button className="mt-5" onClick={onCreate}>Create customer</Button> : null}
    </Card>
  );
}

function CustomerOperations({
  activeTab,
  organization,
  onChanged,
  onTabChange,
}: {
  activeTab: CustomerTab;
  organization: PlatformOrganization;
  onChanged: (message: string) => Promise<void>;
  onTabChange: (tab: CustomerTab) => void;
}) {
  if (activeTab === 'domains') return <DomainsPanel organization={organization} onChanged={onChanged} />;
  if (activeTab === 'users') {
    return <div className="space-y-5">{organization.userRequests.length ? <ApprovalRequestsPanel organization={organization} onChanged={onChanged} /> : null}<MembersPanel organization={organization} onChanged={onChanged} /></div>;
  }
  if (activeTab === 'license') return <LicensePanel organization={organization} onSaved={() => onChanged('License updated.')} />;

  const activeUsers = organization.members.filter((member) => member.status === 'active').length;
  const activeDomains = organization.domains.filter((domain) => domain.active).length;
  const pendingApprovals = organization.userRequests.filter((request) => request.status === 'pending_management_approval').length;
  const usedSeats = organization.license?.usedSeats ?? organization.members.filter((member) => member.status !== 'revoked').length;
  const seatLimit = organization.license?.seatLimit ?? 0;
  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 px-1 sm:flex-row sm:items-start sm:justify-between">
        <div><h2 className="text-xl font-semibold tracking-tight">{organization.name}</h2><p className="mt-1 text-sm text-muted-foreground">{organization.primaryDomain || 'No primary domain'} · {organization.workosOrganizationId ?? 'No identity provider linked'}</p></div>
        <StatusPill status={organization.status} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard detail={`${activeUsers} active members`} icon={UserGroupIcon} iconTone="bg-teal-500/12 text-teal-700" label="Users" onClick={() => onTabChange('users')} value={String(organization.members.length)} />
        <MetricCard detail={`${Math.max(0, seatLimit - usedSeats)} seats available`} icon={LicenseIcon} iconTone="bg-violet-500/12 text-violet-700" label="Licensed seats" onClick={() => onTabChange('license')} value={`${usedSeats}/${seatLimit}`} />
        <MetricCard detail={`${activeDomains} active deployment targets`} icon={Globe02Icon} iconTone="bg-sky-500/12 text-sky-700" label="Domains" onClick={() => onTabChange('domains')} value={String(organization.domains.length)} />
        <MetricCard detail="Customer access requests awaiting review" icon={UserAdd01Icon} iconTone="bg-amber-500/14 text-amber-700" label="Pending approvals" onClick={() => onTabChange('users')} value={String(pendingApprovals)} />
      </div>
      <OperationalTableCard information="A concise readiness view of the customer’s identity, entitlement, deployment, and access state." informationLabel="Customer readiness information" title="Customer readiness">
        <Table className="min-w-[720px]"><TableHeader><TableRow className="hover:bg-transparent"><TableHead className="pl-4">Area</TableHead><TableHead>Current</TableHead><TableHead>Status</TableHead><TableHead className="pr-4 text-right">Action</TableHead></TableRow></TableHeader><TableBody>
          <TableRow><TableCell className="pl-4 font-semibold">Organization</TableCell><TableCell>{organization.workosOrganizationId ?? 'Identity provider not linked'}</TableCell><TableCell><StatusPill status={organization.status} /></TableCell><TableCell className="pr-4 text-right"><span className="text-xs text-muted-foreground">{organization.slug}</span></TableCell></TableRow>
          <TableRow><TableCell className="pl-4 font-semibold">License entitlement</TableCell><TableCell>{organization.license?.expiryDate ? `Expires ${organization.license.expiryDate}` : 'No license configured'}</TableCell><TableCell><StatusPill status={organization.license?.status ?? 'Not configured'} /></TableCell><TableCell className="pr-4 text-right"><TableActionButton onClick={() => onTabChange('license')}>Manage license</TableActionButton></TableCell></TableRow>
          <TableRow><TableCell className="pl-4 font-semibold">Deployment targets</TableCell><TableCell>{activeDomains} of {organization.domains.length} active</TableCell><TableCell><StatusPill status={activeDomains ? 'Configured' : 'Needs setup'} /></TableCell><TableCell className="pr-4 text-right"><TableActionButton onClick={() => onTabChange('domains')}>Manage domains</TableActionButton></TableCell></TableRow>
          <TableRow><TableCell className="pl-4 font-semibold">Customer access</TableCell><TableCell>{activeUsers} active · {pendingApprovals} pending approval</TableCell><TableCell><StatusPill status={pendingApprovals ? 'Needs review' : 'Current'} /></TableCell><TableCell className="pr-4 text-right"><TableActionButton onClick={() => onTabChange('users')}>Manage users</TableActionButton></TableCell></TableRow>
        </TableBody></Table>
      </OperationalTableCard>
    </div>
  );
}

function ApprovalRequestsPanel({ organization, onChanged }: { organization: PlatformOrganization; onChanged: (message: string) => Promise<void> }) {
  const [busyId, setBusyId] = useState<string | null>(null);
  async function decide(requestId: string, decision: 'approve' | 'reject') {
    setBusyId(requestId);
    try {
      await decidePlatformUserRequest(organization.id, requestId, decision);
      await onChanged(decision === 'approve' ? 'Seat request approved and invitation queued.' : 'Seat request declined.');
    } finally { setBusyId(null); }
  }
  return (
    <OperationalTableCard
      information="Approval expands capacity when required, creates the member, and queues identity access provisioning."
      informationLabel="Seat approval information"
      title="Pending seat approvals"
    >
      <Table className="min-w-[720px]">
        <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="pl-4">Requester</TableHead><TableHead>Role</TableHead><TableHead>Requested</TableHead><TableHead>Status</TableHead><TableHead className="pr-4 text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {organization.userRequests.map((request) => (
            <TableRow key={request.id}>
              <TableCell className="pl-4"><p className="font-semibold">{request.fullName}</p><p className="text-xs text-muted-foreground">{request.email}</p></TableCell>
              <TableCell>{request.requestedRole === 'org_admin' ? 'Admin' : 'Member'}</TableCell>
              <TableCell>{formatDate(request.createdAt)}</TableCell>
              <TableCell><StatusPill status={request.status} /></TableCell>
              <TableCell className="pr-4"><div className="flex justify-end gap-2"><Button disabled={busyId === request.id} onClick={() => void decide(request.id, 'reject')} size="sm" type="button" variant="destructive">Reject</Button><Button disabled={busyId === request.id} onClick={() => void decide(request.id, 'approve')} size="sm" type="button">Approve & add seat</Button></div></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </OperationalTableCard>
  );
}

function CreateOrganizationDialog({
  onClose,
  onCreated,
  open,
}: {
  onClose: () => void;
  onCreated: () => Promise<void>;
  open: boolean;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [domain, setDomain] = useState('');
  const [workosOrganizationId, setWorkosOrganizationId] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await createPlatformOrganization({
        name,
        slug,
        workosOrganizationId: workosOrganizationId || null,
        domains: domain
          ? [{ hostname: domain, environment: 'production', active: true }]
          : [],
      });
      setName('');
      setSlug('');
      setDomain('');
      setWorkosOrganizationId('');
      await onCreated();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog onOpenChange={(nextOpen) => { if (!nextOpen && !saving) onClose(); }} open={open}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={(event) => { void submit(event); }}>
          <DialogHeader>
            <DialogTitle>Create customer</DialogTitle>
            <DialogDescription>Add the organization now; domains, users, and licensing can be managed afterward.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 sm:grid-cols-2">
            <LabeledInput autoFocus label="Name" onChange={(event) => setName(event.target.value)} required value={name} />
            <LabeledInput label="Slug" onChange={(event) => setSlug(event.target.value)} required value={slug} />
            <LabeledInput label="Tools domain" onChange={(event) => setDomain(event.target.value)} value={domain} />
            <LabeledInput label="Identity organization ID" onChange={(event) => setWorkosOrganizationId(event.target.value)} value={workosOrganizationId} />
          </div>
          <DialogFooter>
            <Button className="border-0" disabled={saving} onClick={onClose} type="button" variant="destructive">Cancel</Button>
            <Button disabled={saving} type="submit">{saving ? 'Creating…' : 'Create customer'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DomainsPanel({
  organization,
  onChanged,
}: {
  organization: PlatformOrganization;
  onChanged: (message: string) => Promise<void>;
}) {
  const [hostname, setHostname] = useState('');
  const [environment, setEnvironment] = useState<'local' | 'staging' | 'production'>('production');
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await createPlatformDomain(organization.id, {
        hostname,
        environment,
        active: true,
      });
      setHostname('');
      setEnvironment('production');
      await onChanged('Domain added.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleDomain(domainId: string, active: boolean) {
    await updatePlatformDomain(organization.id, domainId, { active });
    await onChanged(active ? 'Domain activated.' : 'Domain deactivated.');
  }

  return (
    <Card className="gap-0 py-0" size="sm">
      <CardHeader className="p-5 pb-4">
        <CardTitle>Deployment domains</CardTitle>
        <CardDescription>Add authorized customer hosts and control whether each deployment is active.</CardDescription>
        <CardAction><Badge variant="secondary">{organization.domains.filter((domain) => domain.active).length} active</Badge></CardAction>
      </CardHeader>
      <CardContent className="px-5 pb-4">
        <form className="grid gap-3 md:grid-cols-[minmax(0,1fr)_180px_auto]" onSubmit={(event) => { void submit(event); }}>
          <LabeledInput label="Hostname" onChange={(event) => setHostname(event.target.value)} required value={hostname} />
          <PlatformSelect label="Environment" onChange={(value) => setEnvironment(value)} options={domainEnvironmentOptions} value={environment} />
          <div className="flex items-end"><Button disabled={saving} type="submit">{saving ? 'Adding…' : 'Add domain'}</Button></div>
        </form>
      </CardContent>
      <Table className="min-w-[640px]">
        <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="pl-4">Hostname</TableHead><TableHead>Environment</TableHead><TableHead>Created</TableHead><TableHead>Status</TableHead><TableHead className="pr-4 text-right">Action</TableHead></TableRow></TableHeader>
        <TableBody>
          {organization.domains.map((domain) => (
            <TableRow key={domain.id}>
              <TableCell className="pl-4 font-semibold">{domain.hostname}</TableCell>
              <TableCell className="capitalize">{domain.environment}</TableCell>
              <TableCell>{formatDate(domain.createdAt)}</TableCell>
              <TableCell><StatusPill status={domain.active ? 'active' : 'suspended'} /></TableCell>
              <TableCell className="pr-4 text-right"><Button onClick={() => { void toggleDomain(domain.id, !domain.active); }} size="sm" variant={domain.active ? 'destructive' : 'outline'}>{domain.active ? 'Deactivate' : 'Activate'}</Button></TableCell>
            </TableRow>
          ))}
          {!organization.domains.length ? <TableRow><TableCell className="h-20 text-center text-muted-foreground" colSpan={5}>No deployment domains configured.</TableCell></TableRow> : null}
        </TableBody>
      </Table>
    </Card>
  );
}

function LicensePanel({
  organization,
  onSaved,
}: {
  organization: PlatformOrganization;
  onSaved: () => Promise<void>;
}) {
  const [status, setStatus] = useState<LicenseStatus>('active');
  const [startDate, setStartDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [seatLimit, setSeatLimit] = useState(1);
  const [limitsSummary, setLimitsSummary] = useState('');
  const [licensedDomains, setLicensedDomains] = useState('');
  const [deploymentModel, setDeploymentModel] = useState<DeploymentModel>('on_prem');
  const [releaseChannel, setReleaseChannel] = useState<ReleaseChannel>('stable');
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const draft = getLicenseDraft(organization);
    setStatus(draft.status);
    setStartDate(draft.startDate);
    setExpiryDate(draft.expiryDate);
    setSeatLimit(draft.seatLimit);
    setLimitsSummary(draft.limitsSummary);
    setLicensedDomains(draft.licensedDomains);
    setDeploymentModel(draft.deploymentModel);
    setReleaseChannel(draft.releaseChannel);
    setSavedSnapshot(serializeLicenseDraft(draft));
  }, [organization]);

  const draft = {
    status,
    startDate,
    expiryDate,
    seatLimit,
    limitsSummary,
    licensedDomains,
    deploymentModel,
    releaseChannel,
  };
  const hasUnsavedChanges = serializeLicenseDraft(draft) !== savedSnapshot;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await upsertPlatformLicense(organization.id, {
        status,
        startDate: startDate || null,
        expiryDate,
        seatLimit,
        limitsSummary,
        licensedDomains: licensedDomains
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        deploymentModel,
        releaseChannel,
      });
      setSavedSnapshot(serializeLicenseDraft(draft));
      await onSaved();
    } finally {
      setSaving(false);
    }
  }

  function discardEdits() {
    const next = getLicenseDraft(organization);
    setStatus(next.status);
    setStartDate(next.startDate);
    setExpiryDate(next.expiryDate);
    setSeatLimit(next.seatLimit);
    setLimitsSummary(next.limitsSummary);
    setLicensedDomains(next.licensedDomains);
    setDeploymentModel(next.deploymentModel);
    setReleaseChannel(next.releaseChannel);
    setSavedSnapshot(serializeLicenseDraft(next));
  }

  const usedSeats = organization.license?.usedSeats ?? 0;
  const currentSeatLimit = organization.license?.seatLimit ?? seatLimit;
  const seatUsage = currentSeatLimit > 0 ? Math.min(100, Math.round((usedSeats / currentSeatLimit) * 100)) : 0;
  const domainsCount = organization.domains.filter((domain) => domain.active).length;
  const licensePeriod = getLicensePeriodProgress(startDate || null, expiryDate || undefined);

  return (
    <form className="flex h-full min-h-0 flex-col gap-4" onSubmit={(event) => { void submit(event); }}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div><h2 className="text-xl font-semibold tracking-tight">License management</h2><p className="mt-1 text-sm text-muted-foreground">{organization.primaryDomain || 'No primary domain configured'}</p></div>
        <div className="flex flex-wrap items-center gap-2"><LicenseStatusBadge status={status} />{hasUnsavedChanges ? <Button className="border-0" disabled={saving} onClick={discardEdits} size="sm" type="button" variant="destructive">Discard</Button> : null}<Button disabled={saving || !hasUnsavedChanges} size="sm" type="submit">{saving ? 'Saving…' : 'Save changes'}</Button></div>
      </div>

      <div className="grid shrink-0 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <LicenseMetric detail="Current customer entitlement" icon={LicenseIcon} iconTone="bg-emerald-500/12 text-emerald-700" label="Status" value={status} />
        <LicenseMetric detail={`${startDate || 'Not set'} — ${expiryDate || 'Not set'}`} icon={CalendarClockIcon} iconTone="bg-violet-500/12 text-violet-700" label="License period" value={licensePeriod.label}><SegmentedMeter ariaLabel={`${licensePeriod.percent}% of the license period remaining`} colorClassName="bg-violet-600" ratio={licensePeriod.percent / 100} /></LicenseMetric>
        <LicenseMetric detail={`${Math.max(0, currentSeatLimit - usedSeats)} seats available`} icon={UserGroupIcon} iconTone="bg-teal-500/12 text-teal-700" label="Licensed seats" value={<span className="flex items-baseline justify-between text-sm font-normal"><span className="text-muted-foreground">Seats used</span><span className="font-semibold tabular-nums">{usedSeats} <span className="font-normal text-muted-foreground">of {currentSeatLimit}</span></span></span>}><SegmentedMeter ariaLabel={`${usedSeats} of ${currentSeatLimit} seats used`} ratio={seatUsage / 100} /></LicenseMetric>
        <LicenseMetric detail={`${organization.domains.length} configured`} icon={Globe02Icon} iconTone="bg-sky-500/12 text-sky-700" label="Active domains" value={String(domainsCount)} />
      </div>

      <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-2">
        <Card className="h-full" size="sm">
          <CardHeader><CardTitle>License terms</CardTitle><CardDescription>Set the access window and seat capacity for this customer.</CardDescription></CardHeader>
          <CardContent className="grid gap-4">
            <PlatformSelect
              label="Status"
              onChange={(value) => setStatus(value)}
              options={licenseStatusOptions}
              value={status}
            />
            <LabeledInput
              label="Seat quota"
              min={1}
              onChange={(event) => setSeatLimit(Number(event.target.value))}
              required
              type="number"
              value={seatLimit}
            />
            <DatePicker label="Start date" onChange={setStartDate} value={startDate} />
            <DatePicker label="End date" onChange={setExpiryDate} required value={expiryDate} />
          </CardContent>
        </Card>

        <Card className="h-full" size="sm">
          <CardHeader><CardTitle>Access and limits</CardTitle><CardDescription>Define where the license is valid and what it includes.</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <PlatformSelect
                label="Deployment model"
                onChange={setDeploymentModel}
                options={deploymentModelOptions}
                value={deploymentModel}
              />
              <PlatformSelect
                label="Release channel"
                onChange={setReleaseChannel}
                options={releaseChannelOptions}
                value={releaseChannel}
              />
            </div>
            <LabeledInput
              label="Authorized domains"
              onChange={(event) => setLicensedDomains(event.target.value)}
              required
              value={licensedDomains}
            />
            <label className="block text-xs font-medium text-muted-foreground">
              <span className="mb-2 block">Limits summary</span>
              <Textarea
                className="min-h-24 resize-y rounded-xl border-border/80 bg-card px-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                onChange={(event) => setLimitsSummary(event.target.value)}
                required
                value={limitsSummary}
              />
            </label>
          </CardContent>
        </Card>
      </div>
    </form>
  );
}

type LicenseDraft = {
  status: LicenseStatus;
  startDate: string;
  expiryDate: string;
  seatLimit: number;
  limitsSummary: string;
  licensedDomains: string;
  deploymentModel: DeploymentModel;
  releaseChannel: ReleaseChannel;
};

function getLicenseDraft(organization: PlatformOrganization): LicenseDraft {
  return {
    status: (organization.license?.status as LicenseStatus | undefined) ?? 'active',
    startDate: organization.license?.startDate ?? '',
    expiryDate: organization.license?.expiryDate ?? '',
    seatLimit: organization.license?.seatLimit ?? 1,
    limitsSummary: organization.license?.limitsSummary ?? 'Tools dashboard access. PDF processing remains client-side.',
    licensedDomains: (organization.license?.licensedDomains ?? organization.domains.map((domain) => domain.hostname)).join(', '),
    deploymentModel: organization.license?.deploymentModel ?? 'on_prem',
    releaseChannel: organization.license?.releaseChannel ?? 'stable',
  };
}

function serializeLicenseDraft(draft: LicenseDraft) {
  return JSON.stringify(draft);
}

function LicenseMetric({ children, detail, icon, iconTone, label, value }: { children?: ReactNode; detail: string; icon: ComponentProps<typeof HugeIcon>['icon']; iconTone: string; label: string; value: ReactNode }) {
  return (
    <Card className="h-full gap-3 py-3" size="sm">
      <CardContent className="flex h-full flex-col gap-3">
        <div className="flex items-center gap-2"><span className={`flex size-7 items-center justify-center rounded-lg ${iconTone}`}><HugeIcon icon={icon} size={15} /></span><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p></div>
        <div className="truncate text-xl font-semibold capitalize tracking-tight">{value}</div>
        {children}
        <p className="mt-auto text-xs text-muted-foreground">{detail}</p>
      </CardContent>
    </Card>
  );
}

function LicenseStatusBadge({ status }: { status: LicenseStatus }) {
  return <StatusPill status={status} />;
}

function MembersPanel({
  organization,
  onChanged,
}: {
  organization: PlatformOrganization;
  onChanged: (message: string) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [memberDraft, setMemberDraft] = useState<{ role: Role; status: UserStatus } | null>(null);
  const [pendingChange, setPendingChange] = useState<{
    member: PlatformOrganization['members'][number];
    draft: { role: Role; status: UserStatus };
  } | null>(null);
  const [updatingMember, setUpdatingMember] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [retryingMemberId, setRetryingMemberId] = useState<string | null>(null);
  const [pendingInvitationAction, setPendingInvitationAction] = useState<{ memberId: string; name: string; action: 'resend' | 'revoke' } | null>(null);

  useEffect(() => {
    setEditingMemberId(null);
    setMemberDraft(null);
    setPendingChange(null);
    setUpdateError(null);
  }, [organization.id]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await createPlatformMember(organization.id, {
        email,
        fullName,
        role,
        status: 'pending_activation',
      });
      setEmail('');
      setFullName('');
      setRole('member');
      await onChanged('Member added. An invitation email has been queued.');
      setAddOpen(false);
    } finally {
      setSaving(false);
    }
  }

  function startEditingMember(member: PlatformOrganization['members'][number]) {
    setUpdateError(null);
    setEditingMemberId(member.id);
    setMemberDraft({ role: member.role, status: member.status });
  }

  function cancelEditingMember() {
    setEditingMemberId(null);
    setMemberDraft(null);
    setUpdateError(null);
  }

  const editingMember = organization.members.find((member) => member.id === editingMemberId) ?? null;
  const hasDraftChanges = Boolean(
    editingMember &&
      memberDraft &&
      (editingMember.role !== memberDraft.role || editingMember.status !== memberDraft.status)
  );
  const filteredMembers = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return organization.members;
    return organization.members.filter((member) =>
      member.fullName.toLowerCase().includes(normalized) || member.email.toLowerCase().includes(normalized)
    );
  }, [organization.members, query]);
  const pageCount = Math.max(1, Math.ceil(filteredMembers.length / 15));
  const pagedMembers = filteredMembers.slice((page - 1) * 15, page * 15);

  useEffect(() => { setPage(1); }, [organization.id, query]);
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);

  async function retryMember(memberId: string) {
    setRetryingMemberId(memberId);
    setUpdateError(null);
    try {
      await retryPlatformMemberSync(organization.id, memberId);
      await onChanged('Identity synchronization queued.');
    } catch (error) {
      setUpdateError(error instanceof Error ? error.message : 'Unable to retry identity synchronization.');
    } finally {
      setRetryingMemberId(null);
    }
  }

  async function confirmInvitationAction() {
    if (!pendingInvitationAction) return;
    setRetryingMemberId(pendingInvitationAction.memberId);
    setUpdateError(null);
    try {
      if (pendingInvitationAction.action === 'resend') {
        await resendPlatformMemberInvitation(organization.id, pendingInvitationAction.memberId);
      } else {
        await revokePlatformMemberInvitation(organization.id, pendingInvitationAction.memberId);
      }
      const action = pendingInvitationAction.action;
      setPendingInvitationAction(null);
      await onChanged(action === 'resend' ? 'Invitation resent.' : 'Invitation revoked.');
    } catch (error) {
      setUpdateError(error instanceof Error ? error.message : 'Unable to update invitation.');
    } finally {
      setRetryingMemberId(null);
    }
  }

  function requestMemberSave() {
    if (!editingMember || !memberDraft || !hasDraftChanges) return;
    setPendingChange({ member: editingMember, draft: memberDraft });
  }

  async function confirmMemberChange() {
    if (!pendingChange) return;
    setUpdatingMember(true);
    setUpdateError(null);
    try {
      const update: { role?: Role; status?: UserStatus } = {};
      if (pendingChange.member.role !== pendingChange.draft.role) update.role = pendingChange.draft.role;
      if (pendingChange.member.status !== pendingChange.draft.status) update.status = pendingChange.draft.status;
      await updatePlatformMember(organization.id, pendingChange.member.id, update);
      setPendingChange(null);
      setEditingMemberId(null);
      setMemberDraft(null);
      await onChanged('Member access updated.');
    } catch (error) {
      setUpdateError(error instanceof Error ? error.message : 'Unable to update member.');
    } finally {
      setUpdatingMember(false);
    }
  }

  async function importPlatformMembers(users: UserImportInput[]): Promise<Member[]> {
    const imported: Member[] = [];
    for (const user of users) {
      const result = await createPlatformMember(organization.id, { ...user, status: 'pending_activation' });
      const now = new Date().toISOString();
      imported.push({
        id: result.id,
        ...user,
        status: 'pending_activation',
        createdAt: now,
        invitationLastSentAt: now,
        invitationCanResendAt: null,
        syncState: 'awaiting_sync',
        observedStatus: null,
        observedRole: null,
        observedAt: null,
        invitationState: 'pending',
        invitationExpiresAt: null,
        lastLoginAt: null,
      });
    }
    return imported;
  }

  return (
    <Card className="gap-0 py-0" size="sm">
      <CardHeader className="p-5 pb-4">
        <CardTitle>Organization users</CardTitle>
        <CardAction className="col-span-2 row-start-2 flex w-full min-w-0 items-center gap-2 sm:col-span-1 sm:col-start-2 sm:row-start-1 sm:w-auto">
          <label className="relative min-w-0 flex-1 sm:w-56 sm:flex-none"><span className="sr-only">Search users</span><HugeIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" icon={Search01Icon} size={14} /><Input className="h-8 w-full bg-white pl-9" onChange={(event) => setQuery(event.target.value)} placeholder="Search users" value={query} /></label>
          <Button aria-label="Import users from CSV" onClick={() => setImportOpen(true)} size="icon-sm" title="Import users" type="button" variant="outline"><HugeIcon icon={FileUploadIcon} size={15} /></Button>
          <Button aria-label="Export organization users to CSV" onClick={() => downloadMembersCsv(filteredMembers)} size="icon-sm" title="Export users" type="button" variant="outline"><HugeIcon icon={FileDownloadIcon} size={15} /></Button>
          <Button aria-label="Save user changes" disabled={!hasDraftChanges || updatingMember} onClick={requestMemberSave} size="icon-sm" title="Save user changes" type="button"><HugeIcon icon={FloppyDiskIcon} size={15} /></Button>
          <Button aria-label="Add user" onClick={() => setAddOpen(true)} size="icon-sm" title="Add user" type="button"><HugeIcon icon={UserAdd01Icon} size={15} /></Button>
        </CardAction>
      </CardHeader>
      <Table className="min-w-[920px]">
        <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="pl-4">Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Auth layer</TableHead><TableHead>Created</TableHead><TableHead className="pr-4 text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
        {pagedMembers.map((member) => {
          const isEditing = editingMemberId === member.id;
          const currentDraft = isEditing && memberDraft ? memberDraft : { role: member.role, status: member.status };
          return (
            <TableRow key={member.id}>
              <TableCell className="pl-4 font-semibold">{member.fullName}</TableCell>
              <TableCell className="text-muted-foreground">{member.email}</TableCell>
              {isEditing ? (
                <TableCell><CustomSelect
                  ariaLabel="Change role"
                  fullWidth
                  onChange={(value) => setMemberDraft((current) => current ? { ...current, role: value } : current)}
                  options={roleOptions}
                  value={currentDraft.role}
                  variant="compact"
                /></TableCell>
              ) : (
                <TableCell>{formatMemberRole(member.role)}</TableCell>
              )}
              {isEditing ? (
                <TableCell><CustomSelect
                  ariaLabel="Change status"
                  fullWidth
                  onChange={(value) => setMemberDraft((current) => current ? { ...current, status: value } : current)}
                  options={userStatusOptions}
                  value={currentDraft.status}
                  variant="compact"
                /></TableCell>
              ) : (
                <TableCell><StatusPill status={member.status} /></TableCell>
              )}
              <TableCell><div className="flex items-center gap-2"><StatusPill status={formatWorkOSSyncState(member.syncState)} /><p className="text-[11px] text-muted-foreground">{member.syncErrorCode ? `Error: ${member.syncErrorCode.replace('_', ' ')}` : member.invitationExpiresAt && member.status === 'pending_activation' ? `Expires ${formatDate(member.invitationExpiresAt)}` : member.observedAt ? `Checked ${formatDate(member.observedAt)}` : 'Awaiting observation'}</p></div></TableCell>
              <TableCell className="text-muted-foreground">{formatDate(member.createdAt)}</TableCell>
              <TableCell className="pr-4"><div className="flex justify-end">
                {isEditing ? (
                  <Button className="border-0" onClick={cancelEditingMember} size="sm" type="button" variant="destructive">Cancel</Button>
                ) : (
                  <div className="flex items-center gap-2">{member.status === 'pending_activation' ? <><Button className="bg-muted hover:bg-muted/80" disabled={retryingMemberId === member.id} onClick={() => setPendingInvitationAction({ memberId: member.id, name: member.fullName, action: 'resend' })} size="sm" type="button" variant="ghost">Resend</Button><Button className="bg-destructive/12 text-destructive hover:bg-destructive/20 hover:text-destructive" disabled={retryingMemberId === member.id} onClick={() => setPendingInvitationAction({ memberId: member.id, name: member.fullName, action: 'revoke' })} size="sm" type="button" variant="ghost">Revoke</Button></> : null}{member.syncState === 'failed' || member.syncState === 'drifted' ? <Button disabled={retryingMemberId === member.id} onClick={() => { void retryMember(member.id); }} size="sm" type="button" variant="ghost">{retryingMemberId === member.id ? 'Queuing' : 'Retry sync'}</Button> : null}<Button disabled={Boolean(editingMemberId)} onClick={() => startEditingMember(member)} size="sm" type="button" variant="outline">Edit</Button></div>
                )}
              </div></TableCell>
            </TableRow>
          );
        })}
        {!filteredMembers.length ? <TableRow><TableCell className="h-20 text-center text-muted-foreground" colSpan={7}>No users match this search.</TableCell></TableRow> : null}
        </TableBody>
      </Table>
      <Pagination className="border-t px-4 py-3"><PaginationStatus>Page {page} of {pageCount} · {filteredMembers.length} of {organization.members.length} users</PaginationStatus><PaginationControls><PaginationPrevious disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))} /><PaginationNext disabled={page === pageCount} onClick={() => setPage((current) => Math.min(pageCount, current + 1))} /></PaginationControls></Pagination>

      <Dialog onOpenChange={(open) => { if (!saving) setAddOpen(open); }} open={addOpen}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={(event) => { void submit(event); }}>
            <DialogHeader><DialogTitle>Add user</DialogTitle><DialogDescription>Create a pending member and queue an account invitation.</DialogDescription></DialogHeader>
            <div className="space-y-4 py-5"><LabeledInput label="Full name" onChange={(event) => setFullName(event.target.value)} required value={fullName} /><LabeledInput label="Email" onChange={(event) => setEmail(event.target.value)} required type="email" value={email} /><PlatformSelect className="w-full" label="Role" onChange={(value) => setRole(value)} options={roleOptions} value={role} /></div>
            <DialogFooter><Button className="border-0" disabled={saving} onClick={() => setAddOpen(false)} type="button" variant="destructive">Cancel</Button><Button disabled={saving} type="submit">{saving ? 'Adding…' : 'Add user'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <UserImportDialog
        importCandidates={importPlatformMembers}
        onClose={() => setImportOpen(false)}
        onImported={(imported) => {
          setImportOpen(false);
          void onChanged(`${imported.length} ${imported.length === 1 ? 'user was' : 'users were'} imported and invitations were queued.`);
        }}
        open={importOpen}
        validateCandidates={(candidates) => Promise.resolve(validatePlatformUserImport(candidates, organization))}
      />

      {pendingChange ? (
        <Dialog onOpenChange={(open) => { if (!open && !updatingMember) setPendingChange(null); }} open>
          <DialogContent>
            <DialogHeader><DialogTitle>Confirm user update</DialogTitle><DialogDescription>This will update {pendingChange.member.fullName}'s role to {formatMemberRole(pendingChange.draft.role)} and status to {formatMemberStatus(pendingChange.draft.status)}.</DialogDescription></DialogHeader>
            {updateError ? <p className="text-xs font-medium text-destructive">{updateError}</p> : null}
            <DialogFooter><Button className="border-0" disabled={updatingMember} onClick={() => setPendingChange(null)} variant="destructive">Cancel</Button><Button disabled={updatingMember} onClick={() => { void confirmMemberChange(); }}>{updatingMember ? 'Updating' : 'Confirm'}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
      {pendingInvitationAction ? <Dialog onOpenChange={(open) => { if (!open && !retryingMemberId) setPendingInvitationAction(null); }} open><DialogContent><DialogHeader><DialogTitle>{pendingInvitationAction.action === 'resend' ? 'Resend invitation' : 'Revoke invitation'}</DialogTitle><DialogDescription>{pendingInvitationAction.action === 'resend' ? `Send another invitation to ${pendingInvitationAction.name}?` : `Revoke ${pendingInvitationAction.name}'s pending access in the console and identity service?`}</DialogDescription></DialogHeader>{updateError ? <p className="text-xs font-medium text-destructive">{updateError}</p> : null}<DialogFooter><Button className="border-0" disabled={Boolean(retryingMemberId)} onClick={() => setPendingInvitationAction(null)} variant="destructive">Cancel</Button><Button disabled={Boolean(retryingMemberId)} onClick={() => { void confirmInvitationAction(); }} variant={pendingInvitationAction.action === 'revoke' ? 'destructive' : 'default'}>{retryingMemberId ? 'Working' : pendingInvitationAction.action === 'resend' ? 'Resend invitation' : 'Revoke invitation'}</Button></DialogFooter></DialogContent></Dialog> : null}
    </Card>
  );
}

function formatMemberRole(role: Role) {
  return role === 'org_admin' ? 'Admin' : 'Member';
}

function formatMemberStatus(status: UserStatus) {
  if (status === 'pending_activation') return 'Pending activation';
  return `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
}

function validatePlatformUserImport(candidates: UserImportCandidate[], organization: PlatformOrganization): UserImportValidation {
  const existing = new Set(organization.members.map((member) => member.email.toLowerCase()));
  const uploaded = new Set<string>();
  const usedSeats = organization.members.filter((member) => member.status !== 'revoked').length;
  const availableSeats = Math.max(0, (organization.license?.seatLimit ?? 0) - usedSeats);
  let accepted = 0;
  const rows = candidates.map((candidate, index) => {
    const parsed = userImportInputSchema.safeParse(candidate);
    const user = parsed.success ? parsed.data : {
      fullName: candidate.fullName.trim(),
      email: candidate.email.trim(),
      role: candidate.role === 'org_admin' ? 'org_admin' as const : 'member' as const,
    };
    const email = user.email.toLowerCase();
    const errors: string[] = [];
    if (!parsed.success) {
      errors.push(...parsed.error.issues.map((issue) => issue.path[0] === 'role'
        ? 'Role must be member or org_admin.'
        : issue.path[0] === 'email'
          ? 'Enter a valid email address.'
          : 'Full name is required and must be 120 characters or fewer.'));
    }
    if (uploaded.has(email)) errors.push('Email is duplicated in this file.');
    if (existing.has(email)) errors.push('A user with this email already exists.');
    if (!errors.length && accepted >= availableSeats) errors.push('No licensed seat is available for this user.');
    uploaded.add(email);
    if (!errors.length) accepted += 1;
    return { ...user, email, row: index + 2, valid: errors.length === 0, errors };
  });
  return userImportValidationSchema.parse({
    rows,
    ready: rows.filter((row) => row.valid).length,
    failed: rows.filter((row) => !row.valid).length,
    availableSeats,
  });
}

function PlatformTicketsWorkspace({
  onChanged,
  onStatusFilterChange,
  organizations,
  statusFilter,
}: {
  onChanged: (message: string) => Promise<void>;
  onStatusFilterChange: (status: TicketStatusFilter) => void;
  organizations: PlatformOrganization[];
  statusFilter: TicketStatusFilter;
}) {
  const [customerFilter, setCustomerFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriorityFilter>('all');
  const [ticketQuery, setTicketQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const ticketRecords = useMemo(
    () =>
      organizations.flatMap((organization) =>
        organization.tickets.map((ticket) => ({
          key: `${organization.id}:${ticket.id}`,
          organization,
          ticket,
        }))
      ),
    [organizations]
  );
  const scopedTickets = useMemo(
    () => customerFilter === 'all' ? ticketRecords : ticketRecords.filter((record) => record.organization.id === customerFilter),
    [customerFilter, ticketRecords]
  );
  const statusScopedTickets = useMemo(
    () => statusFilter === 'all' ? scopedTickets : scopedTickets.filter(({ ticket }) => ticket.status === statusFilter),
    [scopedTickets, statusFilter]
  );
  const visibleTickets = useMemo(() => {
    const query = ticketQuery.trim().toLowerCase();
    return scopedTickets.filter(({ organization, ticket }) =>
      (statusFilter === 'all' || ticket.status === statusFilter)
      && (priorityFilter === 'all' || ticket.priority === priorityFilter)
      && (!query || ticket.subject.toLowerCase().includes(query) || organization.name.toLowerCase().includes(query))
    );
  }, [priorityFilter, scopedTickets, statusFilter, ticketQuery]);
  const selectedRecord = visibleTickets.find((record) => record.key === selectedKey) ?? null;
  const totalPages = Math.max(1, Math.ceil(visibleTickets.length / PLATFORM_TICKETS_PER_PAGE));
  const paginatedTickets = visibleTickets.slice(
    (page - 1) * PLATFORM_TICKETS_PER_PAGE,
    page * PLATFORM_TICKETS_PER_PAGE
  );
  const customerOptions = [{ label: 'All customers', value: 'all' }, ...organizations.map((organization) => ({ label: organization.name, value: organization.id }))];
  const openCount = scopedTickets.filter(({ ticket }) => ticket.status === 'open').length;
  const inProgressCount = scopedTickets.filter(({ ticket }) => ticket.status === 'in_progress').length;
  const highPriorityCount = scopedTickets.filter(({ ticket }) => ticket.priority === 'high' && ticket.status !== 'closed').length;
  const lowPriorityCount = statusScopedTickets.filter(({ ticket }) => ticket.priority === 'low').length;
  const normalPriorityCount = statusScopedTickets.filter(({ ticket }) => ticket.priority === 'normal').length;
  const filteredHighPriorityCount = statusScopedTickets.filter(({ ticket }) => ticket.priority === 'high').length;
  const selectedStatusLabel = statusFilter === 'in_progress'
    ? 'In progress tickets'
    : statusFilter === 'closed'
      ? 'Closed tickets'
      : 'Open tickets';
  const selectedStatusTone = statusFilter === 'in_progress'
    ? 'text-amber-700'
    : statusFilter === 'closed'
      ? 'text-emerald-700'
      : 'text-red-700';

  function resetTicketView() {
    setSelectedKey(null);
    setPage(1);
  }

  useEffect(() => {
    setSelectedKey(null);
    setPage(1);
  }, [statusFilter]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <section className="flex h-full min-h-0 flex-col gap-4">
      <div className={cn('grid shrink-0 grid-cols-2 gap-3 xl:grid-cols-4', selectedRecord && 'hidden lg:grid')}>
        {statusFilter === 'all' ? (
          <>
            <TicketMetric label="Tickets" tone="text-foreground" value={scopedTickets.length} />
            <TicketMetric label="Open" tone="text-red-700" value={openCount} />
            <TicketMetric label="In progress" tone="text-amber-700" value={inProgressCount} />
            <TicketMetric label="High priority active" tone="text-violet-700" value={highPriorityCount} />
          </>
        ) : (
          <>
            <TicketMetric label={selectedStatusLabel} tone={selectedStatusTone} value={statusScopedTickets.length} />
            <TicketMetric label="Low priority" tone="text-slate-700" value={lowPriorityCount} />
            <TicketMetric label="Normal priority" tone="text-sky-700" value={normalPriorityCount} />
            <TicketMetric label="High priority" tone="text-violet-700" value={filteredHighPriorityCount} />
          </>
        )}
      </div>

      <div className={cn('grid min-h-0 flex-1 gap-3', selectedRecord && 'lg:grid-cols-[320px_minmax(0,1fr)]')}>
        <TicketDirectoryCard
          className={cn('min-h-0', selectedRecord && 'hidden bg-card lg:flex')}
          compact={Boolean(selectedRecord)}
          information={visibleTickets.length ? `${visibleTickets.length} ${visibleTickets.length === 1 ? 'conversation' : 'conversations'} in this scope.` : 'No conversations in this scope.'}
          informationLabel="Platform support tickets information"
          toolbar={(
            <TicketDirectoryToolbar
              customer={{
                onChange: (value) => { setCustomerFilter(value); resetTicketView(); },
                options: customerOptions,
                value: customerFilter,
              }}
              onPriorityChange={(value) => { setPriorityFilter(value); resetTicketView(); }}
              onQueryChange={(value) => { setTicketQuery(value); resetTicketView(); }}
              onStatusChange={(value) => { onStatusFilterChange(value); resetTicketView(); }}
              priority={priorityFilter}
              query={ticketQuery}
              status={statusFilter}
            />
          )}
        >
          <div className="flex min-h-0 flex-1 flex-col bg-card">
            {visibleTickets.length ? (
              <>
              <div className="divide-y divide-border/70 px-2 pb-2 lg:hidden">
                {paginatedTickets.map(({ key, organization, ticket }) => (
                  <button className="flex w-full items-start gap-3 rounded-lg px-2 py-3 text-left hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30" key={key} onClick={() => setSelectedKey(key)} type="button">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">{ticket.subject}</p>
                      <p className="mt-1 truncate text-xs text-muted-foreground">{organization.name} · {formatDate(ticket.createdAt)}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{formatPriority(ticket.priority)} priority</p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2"><StatusPill status={ticket.status} /><HugeIcon className="text-muted-foreground" icon={ArrowRight01Icon} size={14} /></div>
                  </button>
                ))}
              </div>

              <div className="hidden min-h-0 flex-1 overflow-y-auto lg:block">
                <Table className={selectedRecord ? 'table-fixed' : 'min-w-[860px]'}>
                  <TableHeader><TableRow className="hover:bg-transparent"><TableHead className={selectedRecord ? 'w-[68%] pl-4' : 'pl-4'}>Subject</TableHead>{selectedRecord ? null : <TableHead>Customer</TableHead>}{selectedRecord ? null : <TableHead>Created</TableHead>}{selectedRecord ? null : <TableHead>Priority</TableHead>}<TableHead>Status</TableHead>{selectedRecord ? null : <TableHead className="pr-4 text-right">Action</TableHead>}</TableRow></TableHeader>
                  <TableBody>
                    {paginatedTickets.map(({ key, organization, ticket }) => {
                      const isSelected = selectedRecord?.key === key;
                      return (
                        <TableRow aria-current={isSelected ? 'true' : undefined} className="cursor-pointer focus-visible:outline-none focus-visible:[&>td]:bg-accent/60 focus-visible:[&>td:first-child]:rounded-l-lg focus-visible:[&>td:last-child]:rounded-r-lg" key={key} onClick={() => setSelectedKey(key)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelectedKey(key); } }} tabIndex={0}>
                          <TableCell className="pl-4"><p className="max-w-full truncate font-semibold" title={ticket.subject}>{ticket.subject}</p>{selectedRecord ? <p className="truncate text-xs text-muted-foreground">{organization.name} · {formatDate(ticket.createdAt)}</p> : null}</TableCell>
                          {selectedRecord ? null : <TableCell>{organization.name}</TableCell>}
                          {selectedRecord ? null : <TableCell className="text-muted-foreground">{formatDate(ticket.createdAt)}</TableCell>}
                          {selectedRecord ? null : <TableCell>{formatPriority(ticket.priority)}</TableCell>}
                          <TableCell><StatusPill status={ticket.status} /></TableCell>
                          {selectedRecord ? null : <TableCell className="pr-4 text-right"><Button onClick={(event) => { event.stopPropagation(); setSelectedKey(key); }} size="sm" variant="ghost">View</Button></TableCell>}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              </>
            ) : <div className="flex min-h-0 flex-1 items-center justify-center p-5 text-center text-sm text-muted-foreground">No support tickets in this scope.</div>}
            <Pagination className="mt-auto shrink-0 border-t px-4 py-3"><PaginationStatus>{paginatedTickets.length} of {visibleTickets.length} shown · Page {page} of {totalPages}</PaginationStatus><PaginationControls><PaginationPrevious disabled={page <= 1} onClick={() => { setSelectedKey(null); setPage((current) => Math.max(1, current - 1)); }} /><PaginationNext disabled={page >= totalPages} onClick={() => { setSelectedKey(null); setPage((current) => Math.min(totalPages, current + 1)); }} /></PaginationControls></Pagination>
          </div>
        </TicketDirectoryCard>

        {selectedRecord ? (
          <TicketDetailPane contextLabel={selectedRecord.organization.name} deleteAttachment={(ticketId, attachmentId) => deletePlatformTicketAttachment(selectedRecord.organization.id, ticketId, attachmentId)} mode="platform" onClose={() => setSelectedKey(null)} onTicketChange={() => { void onChanged('Ticket updated.'); }} replyToActiveTicket={(ticketId, message, attachments) => replyToPlatformTicket(selectedRecord.organization.id, ticketId, message, attachments)} ticket={selectedRecord.ticket} updateActiveTicketStatus={(ticketId, status) => updatePlatformTicketStatus(selectedRecord.organization.id, ticketId, status)} uploadAttachment={(ticketId, file) => uploadPlatformTicketAttachment(selectedRecord.organization.id, ticketId, file)} />
        ) : null}
      </div>
    </section>
  );
}

function TicketMetric({ label, tone, value }: { label: string; tone: string; value: number }) {
  return (
    <Card className="h-full gap-3 py-3" size="sm">
      <CardContent className="flex h-full flex-col gap-3">
        <div className="flex items-center gap-2"><span className={cn('flex size-7 items-center justify-center rounded-lg bg-muted', tone)}><HugeIcon icon={Ticket01Icon} size={15} /></span><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p></div>
        <p className="text-2xl font-semibold tracking-tight">{value}</p>
      </CardContent>
    </Card>
  );
}

function MetricCard({
  detail,
  icon,
  iconTone,
  label,
  onClick,
  value,
}: {
  detail: string;
  icon: ComponentProps<typeof HugeIcon>['icon'];
  iconTone: string;
  label: string;
  onClick: () => void;
  value: string;
}) {
  return (
    <button className="group rounded-xl text-left focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30" onClick={onClick} type="button">
      <Card className="h-full gap-3 py-3 transition-colors group-hover:bg-muted/30" size="sm">
        <CardContent className="flex h-full flex-col gap-3">
          <div className="flex items-center justify-between gap-2"><div className="flex items-center gap-2"><span className={`flex size-7 items-center justify-center rounded-lg ${iconTone}`}><HugeIcon icon={icon} size={15} strokeWidth={1.9} /></span><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p></div><HugeIcon className="text-muted-foreground/60 transition-transform group-hover:translate-x-0.5" icon={ArrowRight01Icon} size={14} /></div>
          <p className="text-2xl font-semibold capitalize tracking-tight">{value}</p>
          <p className="mt-auto text-xs text-muted-foreground">{detail}</p>
        </CardContent>
      </Card>
    </button>
  );
}

function PlatformSelect<T extends string>({
  className,
  compact = false,
  label,
  onChange,
  options,
  value,
}: {
  className?: string;
  compact?: boolean;
  label?: string;
  onChange: (value: T) => void;
  options: ReadonlyArray<{ readonly label: string; readonly value: T }>;
  value: T;
}) {
  return (
    <CustomSelect
      ariaLabel={label}
      className={cn('rounded-xl border-border/80 bg-card shadow-[0_1px_2px_rgba(15,23,42,0.04)]', className)}
      fullWidth
      label={label}
      onChange={onChange}
      options={options}
      value={value}
      variant={compact ? 'compact' : 'field'}
    />
  );
}

function LabeledInput({
  label,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="block text-xs font-medium text-muted-foreground">
      <span className="mb-2 block">{label}</span>
      <Input className={cn('rounded-xl', className)} {...props} />
    </label>
  );
}

function formatPriority(priority: string) {
  return `${priority.charAt(0).toUpperCase()}${priority.slice(1)}`;
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
}
